import { hashKey, useQuery } from '@tanstack/react-query';
import { callBackend } from '../utils/api';
import { holdingsSourceMatches } from './historicalPerformanceBasis';

export const INTRADAY_SECURITY_DETAIL_CONTRACT = 'current-holdings-price-detail.v1';
const ENDPOINT = 'api/performance/current-holdings/price-observations/security-detail';
const digestPattern = /^[a-f0-9]{64}$/;
const conflict = (message) => Object.assign(new Error(message), { status: 409 });
const sourceIdentity = (source) => source?.kind === 'synthetic_ibkr_intraday.v1'
    ? [source.kind, source.fixture_id, source.generation, source.observation_date_utc, source.real_execution_writes]
    : [source?.kind];

function validateDetail(response, { summary, cogniveId, holding, frequency, view, holdingsSource }) {
    if (response?.contract !== INTRADAY_SECURITY_DETAIL_CONTRACT
        || response.observation_date_utc !== summary.observation_date_utc
        || response.read_at !== summary.read_at || response.scope_digest !== summary.scope_digest
        || response.watchlist_revision_id !== summary.watchlist_revision_id
        || response.selected_cognive_id !== cogniveId || response.price_input_digest !== holding.price_input_digest
        || response.bucket_frequency !== frequency || !Array.isArray(response.observed_prices)
        || response.observed_prices.length > 2000
        || response.observed_prices.some((point) => !Number.isFinite(Date.parse(point.observed_at))
            || !Number.isFinite(point.price_usd) || point.price_usd <= 0)
        || response.selected_security_returns?.cognive_id !== cogniveId
        || response.selected_security_returns?.bucket_frequency !== frequency) {
        throw conflict('The selected holding prices no longer match this portfolio read. Refresh and try again.');
    }
    if (view === 'returns') {
        if (hashKey([response.context]) !== hashKey([summary.context])
            || hashKey([response.portfolio_view]) !== hashKey([summary.portfolio_view])
            || response.portfolio_basis_requested !== summary.portfolio_basis_requested
            || response.portfolio_basis_resolved !== summary.portfolio_basis_resolved
            || hashKey(sourceIdentity(response.source)) !== hashKey(sourceIdentity(summary.source))) {
            throw conflict('The selected holding belongs to another intraday source. Refresh returns and try again.');
        }
    } else if (response.source !== 'stored_yahoo_usd_prices'
        || !holdingsSourceMatches(response.holdings_source, holdingsSource)) {
        throw conflict('Stored prices belong to another portfolio. Refresh and try again.');
    }
    return response;
}

// The full read owns portfolio coverage and accounting. A security choice only
// replaces its own path, bound to that accepted read and its exact quote inputs.
export default function useIntradaySecurityDetail({
    summary, cogniveId, frequency, view, sourceQuery, holdingsSource, viewPayload, parentEpoch, enabled,
}) {
    const requested = Boolean(summary && cogniveId && cogniveId !== summary.selected_cognive_id);
    const holding = summary?.holdings?.find((row) => row.cognive_id === cogniveId);
    const inView = view !== 'returns' || summary?.portfolio_view?.level !== 'attribute'
        || summary.portfolio_view.cognive_ids?.includes(cogniveId);
    const bound = Boolean(holding && inView && digestPattern.test(holding.price_input_digest)
        && digestPattern.test(summary?.scope_digest) && Number.isFinite(Date.parse(summary?.read_at)));
    const queryEnabled = Boolean(enabled && requested && bound);
    const query = useQuery({
        queryKey: ['intraday_security_detail', parentEpoch, sourceQuery, viewPayload,
            summary?.scope_digest, summary?.read_at, summary?.observation_date_utc, summary?.source,
            summary?.context, summary?.portfolio_view, frequency, cogniveId, holding?.price_input_digest],
        queryFn: async ({ signal }) => {
            const payload = {
                observation_date: summary.observation_date_utc, cognive_id: cogniveId,
                bucket_frequency: frequency, read_at: summary.read_at,
                expected_scope_digest: summary.scope_digest, expected_price_input_digest: holding.price_input_digest,
            };
            let response;
            if (view === 'returns') {
                const synthetic = summary.portfolio_basis_resolved === 'synthetic';
                response = await callBackend(ENDPOINT, 'POST', {
                    ...payload, portfolio_basis: summary.portfolio_basis_requested,
                    expected_portfolio_basis_resolved: summary.portfolio_basis_resolved,
                    expected_context_digest: summary.context.context_digest, ...viewPayload,
                    ...(holdingsSource ? { constant_portfolio_source: holdingsSource } : {}),
                    ...(synthetic ? { expected_source_fixture_id: summary.source.fixture_id,
                        expected_source_generation: summary.source.generation } : {}),
                }, { signal });
            } else {
                const params = new URLSearchParams(payload);
                for (const [key, value] of new URLSearchParams(sourceQuery)) params.set(key, value);
                response = await callBackend(`${ENDPOINT}?${params}`, 'GET', null, { signal });
            }
            return validateDetail(response, { summary, cogniveId, holding, frequency, view, holdingsSource });
        },
        enabled: queryEnabled, subscribed: queryEnabled, retry: false,
        // A new portfolio read has its own epoch/cutoff/digest. Reopening one
        // accepted security path can reuse it without another financial read.
        staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false,
    });
    const error = requested ? (!bound
        ? conflict('This holding is no longer available in the accepted portfolio read. Refresh observations to choose a current holding.')
        : query.error) : null;
    const selected = requested && !error ? query.data : null;
    return {
        data: !summary || !requested ? summary : { ...summary,
            selected_cognive_id: cogniveId,
            observed_prices: selected?.observed_prices || [],
            selected_security_returns: selected?.selected_security_returns || null,
        },
        loading: requested && !error && !selected && queryEnabled,
        isFetching: queryEnabled && query.isFetching,
        error,
    };
}
